import React, { useState, useEffect } from 'react';
import { useGithubRepos, useGithubContents, useGithubBranches } from '../helpers/useGithubApi';
import { useBulkCreateFiles } from '../helpers/useBulkCreateFiles';
import { Button } from './Button';
import { Input } from './Input';
import { Checkbox } from './Checkbox';
import { Skeleton } from './Skeleton';
import {
  Folder,
  FileCode,
  ChevronRight,
  ArrowLeft,
  Search,
  GitBranch,
  Github,
  Loader2,
  Download,
  ArrowDown,
  Info,
  AlertTriangle,
  ChevronDown,
  Check,
} from 'lucide-react';
import {
  useUserIntegrations,
  useConnectGitHub,
  USER_INTEGRATIONS_QUERY_KEY,
} from '../helpers/useUserSettings';
import { useQueryClient } from '../lib/queryClient';
import { useAuth } from '../helpers/useAuth';
import { toast } from 'sonner';
import { getGithubTree } from '../endpoints/github/tree_GET.schema';
import { getGithubFile } from '../endpoints/github/file_GET.schema';
import { bulkCreateFiles, createGithubRepo } from '../helpers/chatApiClient'; // Zod-free API client
import { createGithubCommit } from '../endpoints/github/commit_POST.schema';
import GithubIcon from './GithubIcon';
import styles from './GithubImportTab.module.css';

// Helper to get/set connected repo info in localStorage
const REPO_STORAGE_KEY = 'tasking_connected_repos';

export type ConnectedRepoInfo = {
  repo: string;
  branch: string;
  connectedAt: string;
};

export const getConnectedRepo = (sessionId: string): ConnectedRepoInfo | null => {
  try {
    const stored = localStorage.getItem(REPO_STORAGE_KEY);
    if (!stored) return null;
    const repos = JSON.parse(stored);
    return repos[sessionId] || null;
  } catch {
    return null;
  }
};

export const setConnectedRepo = (sessionId: string, info: ConnectedRepoInfo | null) => {
  try {
    const stored = localStorage.getItem(REPO_STORAGE_KEY);
    const repos = stored ? JSON.parse(stored) : {};
    if (info) {
      repos[sessionId] = info;
    } else {
      delete repos[sessionId];
    }
    localStorage.setItem(REPO_STORAGE_KEY, JSON.stringify(repos));
  } catch {
    // Ignore storage errors
  }
};

interface GithubImportTabProps {
  sessionId: string;
  onClose: () => void;
}

type SelectedFile = {
  path: string;
  name: string;
  sha: string;
  repo: string;
  branch?: string;
};

type FilePayload = {
  fileName: string;
  filePath: string;
  content: string;
  language: string;
};

type CloneProgress = {
  stage: 'tree' | 'files' | 'creating';
  current: number;
  total: number;
} | null;

const detectLanguage = (fileName: string) => {
  const ext = fileName.split('.').pop()?.toLowerCase() || 'plaintext';
  if (['js', 'jsx', 'ts', 'tsx'].includes(ext)) return 'typescript';
  if (['py'].includes(ext)) return 'python';
  if (['html'].includes(ext)) return 'html';
  if (['css'].includes(ext)) return 'css';
  if (['json'].includes(ext)) return 'json';
  if (['md'].includes(ext)) return 'markdown';
  if (['sql'].includes(ext)) return 'sql';
  if (['java'].includes(ext)) return 'java';
  if (['go'].includes(ext)) return 'go';
  if (['rs'].includes(ext)) return 'rust';
  return 'plaintext';
};

// Helper to add delay between operations
const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

export const GithubImportTab: React.FC<GithubImportTabProps> = ({ sessionId, onClose }) => {
  // State
  const [selectedRepo, setSelectedRepo] = useState<string | null>(null);
  const [selectedBranch, setSelectedBranch] = useState<string | null>(null);
  const [showBranchDropdown, setShowBranchDropdown] = useState(false);
  const [currentPath, setCurrentPath] = useState<string>('');
  const [repoSearch, setRepoSearch] = useState('');
  const [selectedFiles, setSelectedFiles] = useState<SelectedFile[]>([]);
  const [isImporting, setIsImporting] = useState(false);
  const [cloneProgress, setCloneProgress] = useState<CloneProgress>(null);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [existingFilesCount, setExistingFilesCount] = useState(0);
  // 'choose' = show clone vs browse options, 'browse' = file browser, null = repo list
  const [repoAction, setRepoAction] = useState<'choose' | 'browse' | null>(null);
  const [repoPage, setRepoPage] = useState(1);
  const reposPerPage = 30;

  // Queries
  const { data: integrations, isLoading: isLoadingIntegrations } = useUserIntegrations();
  const {
    data: reposData,
    isLoading: isLoadingRepos,
    error: reposError,
    refetch: refetchRepos,
  } = useGithubRepos(
    repoPage,
    reposPerPage,
    !!integrations?.integrations.some((i) => i.provider === 'github'),
  );

  // Handle GitHub token errors
  useEffect(() => {
    if (reposError) {
      console.error('Failed to fetch GitHub repos:', reposError);
      toast.error('Failed to connect to GitHub. Please reconnect your GitHub account.');
    }
  }, [reposError]);

  // Create repo UI state
  const [showCreateRepo, setShowCreateRepo] = useState(false);
  const [createName, setCreateName] = useState('');
  const [createDescription, setCreateDescription] = useState('');
  const [createPrivate, setCreatePrivate] = useState(false);
  const [createPat, setCreatePat] = useState('');
  const [isCreatingRepo, setIsCreatingRepo] = useState(false);
  const { data: contentsData, isLoading: isLoadingContents } = useGithubContents(
    selectedRepo || undefined,
    currentPath,
    selectedBranch || undefined,
  );
  const { data: branchesData, isLoading: isLoadingBranches } = useGithubBranches(
    selectedRepo || undefined,
    !!selectedRepo,
  );

  // Mutations
  const bulkCreate = useBulkCreateFiles();
  const { connect } = useConnectGitHub();

  const hasGithubIntegration = integrations?.integrations.some((i) => i.provider === 'github');

  // GitHub App specific metadata UI state
  const queryClient = useQueryClient();
  const { authState } = useAuth();
  const currentUser = authState.type === 'authenticated' ? authState.user : null;

  const githubAppIntegration = integrations?.integrations.find(
    (i) => i.provider === 'github' && i.metadata && i.metadata.includes('github_app'),
  );
  const oauthGithubIntegration = integrations?.integrations.find((integration) => {
    if (integration.provider !== 'github') return false;
    if (!integration.metadata) return true;
    try {
      const parsed = JSON.parse(integration.metadata as string);
      return parsed.type !== 'github_app';
    } catch (err) {
      return !integration.metadata.includes('github_app');
    }
  });
  let appMetadata: any = null;
  try {
    if (githubAppIntegration && githubAppIntegration.metadata) {
      appMetadata = JSON.parse(githubAppIntegration.metadata as string);
    }
  } catch (e) {
    appMetadata = null;
  }
  // Allow repo creation for any connected GitHub user (OAuth or App — server handles both)
  const canCreateRepo = Boolean(hasGithubIntegration);

  const [allowedReposState, setAllowedReposState] = useState<string[]>(
    appMetadata?.repos?.map((r: any) => r.full_name) || [],
  );
  const [allowFullDownloadState, setAllowFullDownloadState] = useState<boolean>(
    !!appMetadata?.allowFullDownload,
  );
  const isInstaller = currentUser && appMetadata && appMetadata.installerUserId === currentUser.id;

  // Filter repos
  const filteredRepos = reposData?.repos.filter((repo) =>
    repo.full_name.toLowerCase().includes(repoSearch.toLowerCase()),
  );

  // Set default branch when branches are loaded
  useEffect(() => {
    if (branchesData && !selectedBranch) {
      setSelectedBranch(branchesData.defaultBranch);
    }
  }, [branchesData, selectedBranch]);

  // Handlers
  const handleRepoSelect = (repoFullName: string) => {
    setSelectedRepo(repoFullName);
    setSelectedBranch(null); // Will be set when branches load
    setCurrentPath('');
    setSelectedFiles([]);
    setRepoAction('choose'); // Show clone vs browse action chooser
  };

  const handleBranchSelect = (branchName: string) => {
    setSelectedBranch(branchName);
    setShowBranchDropdown(false);
    setCurrentPath('');
    setSelectedFiles([]);
  };

  const handleNavigate = (path: string) => {
    setCurrentPath(path);
  };

  const handleNavigateUp = () => {
    if (repoAction === 'choose') {
      // Going back from repo action chooser to repo list
      setSelectedRepo(null);
      setRepoAction(null);
    } else if (currentPath === '') {
      // Going back from root file browser to action chooser
      setRepoAction('choose');
    } else {
      const parts = currentPath.split('/');
      parts.pop();
      setCurrentPath(parts.join('/'));
    }
  };

  const toggleFileSelection = (file: any) => {
    if (!selectedRepo) return;

    const isSelected = selectedFiles.some((f) => f.sha === file.sha);
    if (isSelected) {
      setSelectedFiles((prev) => prev.filter((f) => f.sha !== file.sha));
    } else {
      setSelectedFiles((prev) => [
        ...prev,
        {
          path: file.path,
          name: file.name,
          sha: file.sha,
          repo: selectedRepo,
        },
      ]);
    }
  };

  const handleImport = async () => {
    if (selectedFiles.length === 0) return;

    setIsImporting(true);
    try {
      // Use parallel fetching with concurrency limit
      const concurrency = 3;
      const filesToCreate: FilePayload[] = [];

      for (let i = 0; i < selectedFiles.length; i += concurrency) {
        const batch = selectedFiles.slice(i, i + concurrency);
        const batchResults = await Promise.all(
          batch.map(async (file) => {
            try {
              const fileData = await getGithubFile({
                repo: file.repo,
                path: file.path,
                branch: file.branch,
              });

              return {
                fileName: file.name,
                filePath: file.path.substring(0, file.path.lastIndexOf('/')) || '/',
                content: fileData.content,
                language: detectLanguage(file.name),
              } satisfies FilePayload;
            } catch (err) {
              console.error(`Failed to fetch ${file.path}`, err);
              return null;
            }
          }),
        );
        filesToCreate.push(...batchResults.filter((f): f is NonNullable<typeof f> => f !== null));

        if (i + concurrency < selectedFiles.length) {
          await delay(500);
        }
      }

      if (filesToCreate.length > 0) {
        await bulkCreate.mutateAsync({
          sessionId,
          files: filesToCreate,
        });

        toast.success(`Imported ${filesToCreate.length} files`);
        onClose();
      } else {
        toast.error('Failed to import any files');
      }
    } catch (error) {
      console.error('Import failed', error);
      toast.error('Failed to fetch file contents');
    } finally {
      setIsImporting(false);
    }
  };

  const handleCloneRepo = async (forceClone = false) => {
    if (!selectedRepo) return;

    // Check if there are existing files and show confirmation
    if (existingFilesCount > 0 && !forceClone) {
      setShowConfirmDialog(true);
      return;
    }

    setShowConfirmDialog(false);
    setCloneProgress({ stage: 'tree', current: 0, total: 0 });

    try {
      // 1. Fetch tree
      const treeData = await getGithubTree({
        repo: selectedRepo,
        branch: selectedBranch || undefined,
      });

      const filesToFetch = treeData.files;
      const totalFiles = filesToFetch.length;

      if (totalFiles === 0) {
        toast.error('No compatible files found in this repository');
        setCloneProgress(null);
        return;
      }

      setCloneProgress({ stage: 'files', current: 0, total: totalFiles });

      const importedFiles: FilePayload[] = [];
      const pendingFilesForPersist: FilePayload[] = [];
      let processedCount = 0;
      const BATCH_SIZE = 3;
      const PERSIST_CHUNK_SIZE = 10;

      // Persist files in smaller chunks to avoid oversized payloads hitting the API.
      const flushPendingFiles = async (force = false) => {
        if (!force && pendingFilesForPersist.length < PERSIST_CHUNK_SIZE) {
          return;
        }

        while (
          pendingFilesForPersist.length > 0 &&
          (force || pendingFilesForPersist.length >= PERSIST_CHUNK_SIZE)
        ) {
          const chunkSize = force
            ? pendingFilesForPersist.length
            : Math.min(pendingFilesForPersist.length, PERSIST_CHUNK_SIZE);
          const chunk = pendingFilesForPersist.splice(0, chunkSize);
          await bulkCreateFiles({
            sessionId,
            files: chunk,
          });
        }
      };

      for (let i = 0; i < totalFiles; i += BATCH_SIZE) {
        // Check if component unmounted or process cancelled? (Not easily doable without AbortController but good enough for now)

        const batch = filesToFetch.slice(i, i + BATCH_SIZE);

        const batchResults = await Promise.all(
          batch.map(async (fileNode) => {
            try {
              const fileData = await getGithubFile({
                repo: selectedRepo,
                path: fileNode.path,
                branch: selectedBranch || undefined,
              });

              return {
                fileName: fileNode.path.split('/').pop() || '',
                filePath: fileNode.path.substring(0, fileNode.path.lastIndexOf('/')) || '/',
                content: fileData.content,
                language: detectLanguage(fileNode.path),
              } satisfies FilePayload;
            } catch (e) {
              console.error(`Failed to fetch ${fileNode.path}`, e);
              return null;
            }
          }),
        );

        const validFiles = batchResults.filter((f): f is NonNullable<typeof f> => f !== null);
        importedFiles.push(...validFiles);
        pendingFilesForPersist.push(...validFiles);
        await flushPendingFiles();

        processedCount += batch.length;
        setCloneProgress((prev) =>
          prev
            ? {
                ...prev,
                current: Math.min(processedCount, totalFiles),
              }
            : null,
        );

        if (i + BATCH_SIZE < totalFiles) {
          await delay(500);
        }
      }

      setCloneProgress({ stage: 'creating', current: importedFiles.length, total: totalFiles });
      await flushPendingFiles(true);

      // 2. Bulk create
      if (importedFiles.length > 0) {
        await queryClient.invalidateQueries({ queryKey: ['files', 'list', sessionId] });
        await queryClient.invalidateQueries({ queryKey: ['sessions', 'detail', sessionId] });

        // Store connected repo info
        setConnectedRepo(sessionId, {
          repo: selectedRepo,
          branch: selectedBranch || branchesData?.defaultBranch || 'main',
          connectedAt: new Date().toISOString(),
        });

        toast.success(`Successfully cloned ${importedFiles.length} files`);
        onClose();
      } else {
        toast.error('Failed to fetch any file contents');
      }
    } catch (error) {
      console.error('Clone failed', error);
      toast.error(
        'Failed to clone repository: ' + (error instanceof Error ? error.message : 'Unknown error'),
      );
    } finally {
      setCloneProgress(null);
    }
  };

  const handleSaveIntegrationSettings = async () => {
    if (!githubAppIntegration) return;
    try {
      const body = {
        provider: 'github',
        providerUserId: githubAppIntegration.providerUserId,
        allowedRepos: allowedReposState,
        allowFullDownload: allowFullDownloadState,
        sessionId,
      };

      const resp = await fetch('/_api/integrations/update', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });

      if (!resp.ok) {
        const txt = await resp.text();
        throw new Error(txt);
      }

      toast.success('Integration settings saved');
      queryClient.invalidateQueries({ queryKey: USER_INTEGRATIONS_QUERY_KEY });
    } catch (err) {
      console.error('Failed to save integration settings', err);
      toast.error('Failed to save integration settings');
    }
  };

  // Render
  if (isLoadingIntegrations) {
    return (
      <div className={styles.loadingContainer}>
        <Skeleton className={styles.skeleton} />
      </div>
    );
  }

  if (!hasGithubIntegration) {
    return (
      <div className={styles.emptyState}>
        <GithubIcon size={48} className={styles.emptyIcon} connected={false} />
        <h3>Connect GitHub</h3>
        <p>Connect your GitHub account to import repositories and files.</p>
        <Button onClick={connect} className={styles.connectButton}>
          Connect GitHub
        </Button>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      {/* Confirmation Dialog */}
      {showConfirmDialog && (
        <div className={styles.progressOverlay}>
          <div className={styles.confirmDialog}>
            <div className={styles.confirmIcon}>
              <AlertTriangle size={32} />
            </div>
            <h3 className={styles.confirmTitle}>Files Will Be Overwritten</h3>
            <p className={styles.confirmText}>
              This session already has{' '}
              <strong>
                {existingFilesCount} file{existingFilesCount !== 1 ? 's' : ''}
              </strong>
              . Cloning <strong>{selectedRepo}</strong> will overwrite any files with matching
              names. This action cannot be undone.
            </p>
            <p className={styles.confirmText} style={{ marginTop: '8px', fontSize: '0.85rem' }}>
              Are you sure you want to continue?
            </p>
            <div className={styles.confirmActions}>
              <Button variant="secondary" onClick={() => setShowConfirmDialog(false)}>
                Cancel
              </Button>
              <Button variant="destructive" onClick={() => handleCloneRepo(true)}>
                Yes, Overwrite &amp; Clone
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Progress Overlay */}
      {cloneProgress && (
        <div className={styles.progressOverlay}>
          <div className={styles.progressCard}>
            <div className={styles.progressTitle}>
              {cloneProgress.stage === 'tree' && (
                <>
                  <Loader2 size={20} className={styles.spinner} />
                  <span>Scanning repository...</span>
                </>
              )}
              {cloneProgress.stage === 'files' && (
                <>
                  <ArrowDown size={20} className={styles.arrowBounce} />
                  <span>Fetching files...</span>
                </>
              )}
              {cloneProgress.stage === 'creating' && (
                <>
                  <Loader2 size={20} className={styles.spinner} />
                  <span>Creating files in workspace...</span>
                </>
              )}
            </div>

            <div className={styles.progressBarContainer}>
              <div
                className={styles.progressBarFill}
                style={{
                  width:
                    cloneProgress.stage === 'tree'
                      ? '30%'
                      : cloneProgress.stage === 'creating'
                        ? '100%'
                        : `${(cloneProgress.current / cloneProgress.total) * 100}%`,
                }}
              />
            </div>

            <div className={styles.progressStats}>
              {cloneProgress.stage === 'tree' && <span>Fetching file list...</span>}
              {cloneProgress.stage === 'files' && (
                <span>
                  {cloneProgress.current} / {cloneProgress.total} files downloaded
                </span>
              )}
              {cloneProgress.stage === 'creating' && <span>Finalizing import...</span>}
            </div>
          </div>
        </div>
      )}

      {/* Header / Breadcrumbs */}
      <div className={styles.header}>
        {selectedRepo && repoAction === 'browse' ? (
          <div className={styles.breadcrumbs}>
            <Button variant="ghost" size="icon-sm" onClick={handleNavigateUp}>
              <ArrowLeft size={16} />
            </Button>
            <span className={styles.breadcrumbItem}>{selectedRepo}</span>
            {currentPath
              .split('/')
              .filter(Boolean)
              .map((part, i) => (
                <React.Fragment key={i}>
                  <ChevronRight size={14} className={styles.separator} />
                  <span className={styles.breadcrumbItem}>{part}</span>
                </React.Fragment>
              ))}

            {/* Branch Selector */}
            <div className={styles.branchSelector}>
              <button
                className={styles.branchButton}
                onClick={() => setShowBranchDropdown(!showBranchDropdown)}
                disabled={isLoadingBranches}
              >
                <GitBranch size={14} />
                <span>{selectedBranch || 'Loading...'}</span>
                <ChevronDown size={14} />
              </button>
              {showBranchDropdown && branchesData && (
                <div className={styles.branchDropdown}>
                  {branchesData.branches.map((branch) => (
                    <button
                      key={branch.name}
                      className={`${styles.branchOption} ${branch.name === selectedBranch ? styles.branchOptionActive : ''}`}
                      onClick={() => handleBranchSelect(branch.name)}
                    >
                      {branch.name === selectedBranch && <Check size={14} />}
                      <span>{branch.name}</span>
                      {branch.protected && <span className={styles.protectedBadge}>protected</span>}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        ) : selectedRepo && repoAction === 'choose' ? (
          <div className={styles.breadcrumbs}>
            <Button variant="ghost" size="icon-sm" onClick={handleNavigateUp}>
              <ArrowLeft size={16} />
            </Button>
            <span className={styles.breadcrumbItem}>{selectedRepo}</span>
          </div>
        ) : (
          <div className={styles.searchContainer}>
            <Search size={16} className={styles.searchIcon} />
            <Input
              placeholder="Search repositories..."
              value={repoSearch}
              onChange={(e) => setRepoSearch(e.target.value)}
              className={styles.searchInput}
            />
          </div>
        )}
      </div>
      {/* GitHub App installer controls — only visible when there are installed repos */}
      {githubAppIntegration && isInstaller && appMetadata?.repos?.length > 0 && (
        <div className={styles.appControls}>
          <div className={styles.appControlsHeader}>
            <strong>Installed Repositories</strong>
            <div className={styles.appControlsActions}>
              <label className={styles.appControlsCheckbox}>
                <Checkbox
                  checked={allowFullDownloadState}
                  onChange={() => setAllowFullDownloadState(!allowFullDownloadState)}
                  disabled={!isInstaller}
                />
                <span>Allow full download</span>
              </label>
              <Button variant="secondary" size="sm" onClick={handleSaveIntegrationSettings}>
                Save
              </Button>
            </div>
          </div>

          <div className={styles.installedRepoList}>
            {appMetadata.repos.map((r: any) => (
              <div key={r.id} className={styles.repoItem}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <Checkbox
                    checked={allowedReposState.includes(r.full_name)}
                    onChange={() => {
                      setAllowedReposState((prev) =>
                        prev.includes(r.full_name)
                          ? prev.filter((x) => x !== r.full_name)
                          : [...prev, r.full_name],
                      );
                    }}
                  />
                  <span>
                    {r.full_name}
                    {r.private ? ' · private' : ''}
                  </span>
                </label>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Content Area */}
      <div className={styles.content}>
        {selectedRepo && repoAction === 'choose' ? (
          // Repo Action Chooser
          <div className={styles.repoActionChooser}>
            <div className={styles.repoActionHeader}>
              <Github size={24} />
              <div>
                <h3
                  style={{ margin: 0, fontWeight: 600, fontFamily: 'var(--font-family-heading)' }}
                >
                  {selectedRepo}
                </h3>
                <span style={{ fontSize: '0.85rem', color: 'var(--muted-foreground)' }}>
                  What would you like to do with this repository?
                </span>
              </div>
            </div>

            <div className={styles.repoActionCards}>
              <button className={styles.repoActionCard} onClick={() => handleCloneRepo()}>
                <div className={styles.repoActionIcon}>
                  <Download size={24} />
                </div>
                <div>
                  <strong>Clone this repo</strong>
                  <p
                    style={{
                      margin: '4px 0 0',
                      fontSize: '0.85rem',
                      color: 'var(--muted-foreground)',
                    }}
                  >
                    Import all files from this repository into your session.
                    {existingFilesCount > 0 && (
                      <span style={{ display: 'block', color: 'var(--warning)', marginTop: '4px' }}>
                        <AlertTriangle
                          size={12}
                          style={{ verticalAlign: 'middle', marginRight: 4 }}
                        />
                        {existingFilesCount} existing file{existingFilesCount !== 1 ? 's' : ''} may
                        be overwritten
                      </span>
                    )}
                  </p>
                </div>
              </button>

              <button
                className={styles.repoActionCard}
                onClick={() => {
                  setRepoAction('browse');
                  setCurrentPath('');
                }}
              >
                <div className={styles.repoActionIcon}>
                  <Folder size={24} />
                </div>
                <div>
                  <strong>Browse &amp; select files</strong>
                  <p
                    style={{
                      margin: '4px 0 0',
                      fontSize: '0.85rem',
                      color: 'var(--muted-foreground)',
                    }}
                  >
                    Browse the repository and pick specific files to import.
                  </p>
                </div>
              </button>
            </div>
          </div>
        ) : !selectedRepo || repoAction === null ? (
          // Repo List
          <div className={styles.repoList}>
            {/* Error state */}
            {reposError ? (
              <div className={styles.errorRow}>
                <AlertTriangle size={16} style={{ marginRight: 8 }} />
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 600 }}>Failed to fetch repositories</div>
                  <div style={{ color: 'var(--muted-foreground)' }}>
                    {(reposError as Error).message}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <Button variant="secondary" onClick={() => refetchRepos()}>
                    Retry
                  </Button>
                </div>
              </div>
            ) : null}

            {/* Create repo CTA */}
            <div className={styles.createRepoRow}>
              {!showCreateRepo ? (
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <Button variant="secondary" onClick={() => setShowCreateRepo(true)}>
                    Create New Repository
                  </Button>
                  {isLoadingRepos && (
                    <span style={{ color: 'var(--muted-foreground)' }}>Loading repositories…</span>
                  )}
                </div>
              ) : (
                <div className={styles.createRepoForm}>
                  <Input
                    placeholder="Repository name"
                    value={createName}
                    onChange={(e) => setCreateName(e.target.value)}
                    className={styles.createRepoInput}
                  />
                  <Input
                    placeholder="Description (optional)"
                    value={createDescription}
                    onChange={(e) => setCreateDescription(e.target.value)}
                    className={styles.createRepoInput}
                  />
                  <Input
                    placeholder="GitHub Personal Access Token (required to create repos)"
                    value={createPat}
                    onChange={(e) => setCreatePat(e.target.value)}
                    type="password"
                    className={styles.createRepoInput}
                  />
                  <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <Checkbox
                      checked={createPrivate}
                      onChange={() => setCreatePrivate(!createPrivate)}
                    />
                    <span>Private</span>
                  </label>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <Button
                      onClick={async () => {
                        if (!canCreateRepo) {
                          return toast.error(
                            'GitHub is not connected. Please connect your GitHub account first.',
                          );
                        }
                        if (!createName) return toast.error('Repository name is required');
                        setIsCreatingRepo(true);
                        try {
                          // Use Zod-free API client imported at top of file
                          if (!createPat) return toast.error('A GitHub Personal Access Token is required to create repositories. Generate one at github.com/settings/tokens with "repo" scope.');
                          const newRepo = await createGithubRepo({
                            name: createName,
                            description:
                              createDescription || `${createName} — Created from Tasking.tech`,
                            private: createPrivate,
                            auto_init: true,
                            pat: createPat,
                          });
                          toast.success(`Created ${newRepo.full_name} with README`);
                          setShowCreateRepo(false);
                          setCreateName('');
                          setCreateDescription('');
                          setCreatePrivate(false);
                          // Refresh repo list and select newly created repo
                          await refetchRepos();
                          // Store connected repo immediately
                          setConnectedRepo(sessionId, {
                            repo: newRepo.full_name,
                            branch: newRepo.default_branch || 'main',
                            connectedAt: new Date().toISOString(),
                          });
                          setTimeout(() => handleRepoSelect(newRepo.full_name), 300);
                        } catch (err) {
                          console.error('Create repo failed', err);
                          toast.error(
                            err instanceof Error ? err.message : 'Failed to create repository',
                          );
                        } finally {
                          setIsCreatingRepo(false);
                        }
                      }}
                      disabled={isCreatingRepo}
                    >
                      {isCreatingRepo ? 'Creating…' : 'Create'}
                    </Button>
                    <Button variant="ghost" onClick={() => setShowCreateRepo(false)}>
                      Cancel
                    </Button>
                  </div>
                </div>
              )}
            </div>

            {isLoadingRepos ? (
              Array(5)
                .fill(0)
                .map((_, i) => <Skeleton key={i} className={styles.repoSkeleton} />)
            ) : filteredRepos?.length === 0 ? (
              <div className={styles.noResults}>No repositories found</div>
            ) : (
              <>
                {filteredRepos?.map((repo) => (
                  <div
                    key={repo.id}
                    className={styles.repoItem}
                    onClick={() => handleRepoSelect(repo.full_name)}
                  >
                    <div className={styles.repoInfo}>
                      {repo.owner?.avatar_url && (
                        <img
                          src={repo.owner.avatar_url}
                          alt={repo.owner.login}
                          style={{
                            width: '24px',
                            height: '24px',
                            borderRadius: '50%',
                            marginRight: '8px',
                          }}
                        />
                      )}
                      <span className={styles.repoName}>{repo.full_name}</span>
                      {repo.private && <span className={styles.privateBadge}>Private</span>}
                    </div>
                    <div className={styles.repoMeta}>
                      <GitBranch size={12} />
                      <span>{repo.default_branch}</span>
                    </div>
                  </div>
                ))}

                {/* Pagination Controls */}
                {!repoSearch && reposData && reposData.repos.length >= reposPerPage && (
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'center',
                      gap: '8px',
                      padding: '16px',
                      borderTop: '1px solid var(--border)',
                    }}
                  >
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => setRepoPage((p) => Math.max(1, p - 1))}
                      disabled={repoPage === 1 || isLoadingRepos}
                    >
                      Previous
                    </Button>
                    <span
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        padding: '0 12px',
                        color: 'var(--muted-foreground)',
                      }}
                    >
                      Page {repoPage}
                    </span>
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => setRepoPage((p) => p + 1)}
                      disabled={isLoadingRepos || reposData.repos.length < reposPerPage}
                    >
                      Next
                    </Button>
                  </div>
                )}
              </>
            )}
          </div>
        ) : (
          // File Browser
          <div className={styles.fileBrowser}>
            {/* Quick clone option at root of browse mode */}
            {currentPath === '' && (
              <div className={styles.cloneSection}>
                <div className={styles.cloneInfo}>
                  <span>Or clone the entire repository instead of picking files.</span>
                </div>
                <Button
                  onClick={() => handleCloneRepo()}
                  variant="secondary"
                  size="sm"
                  disabled={!!cloneProgress}
                >
                  <Download size={14} style={{ marginRight: '4px' }} />
                  Clone All
                </Button>
              </div>
            )}

            {isLoadingContents ? (
              Array(5)
                .fill(0)
                .map((_, i) => <Skeleton key={i} className={styles.fileSkeleton} />)
            ) : contentsData?.contents.length === 0 ? (
              <div className={styles.noResults}>Empty directory</div>
            ) : (
              contentsData?.contents.map((item) => (
                <div key={item.sha} className={styles.fileItem}>
                  {item.type === 'dir' ? (
                    <div className={styles.dirRow} onClick={() => handleNavigate(item.path)}>
                      <Folder size={16} className={styles.folderIcon} />
                      <span>{item.name}</span>
                    </div>
                  ) : (
                    <div className={styles.fileRow}>
                      <Checkbox
                        checked={selectedFiles.some((f) => f.sha === item.sha)}
                        onChange={() => toggleFileSelection(item)}
                        id={`file-${item.sha}`}
                      />
                      <label htmlFor={`file-${item.sha}`} className={styles.fileLabel}>
                        <FileCode size={16} className={styles.fileIcon} />
                        <span>{item.name}</span>
                        <span className={styles.fileSize}>{(item.size / 1024).toFixed(1)} KB</span>
                      </label>
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        )}
      </div>

      {/* Footer Actions */}
      <div className={styles.footer}>
        <div className={styles.selectionInfo}>
          {selectedFiles.length} file{selectedFiles.length !== 1 ? 's' : ''} selected
        </div>
        <Button
          onClick={handleImport}
          disabled={selectedFiles.length === 0 || isImporting || !!cloneProgress}
        >
          {isImporting ? (
            <>
              <Loader2 size={16} className={styles.spinner} />
              Importing...
            </>
          ) : (
            'Import Selected'
          )}
        </Button>
      </div>
    </div>
  );
};
